// recordingPcAudio — AI 노트 녹음 «PC 소리도 함께 녹음».
// 마이크와 함께 이 PC 에서 나는 소리(화면 공유 오디오 또는 고른 입력 장치 — "스테레오 믹스" 등)를 받아
// WebAudio 로 한 스트림에 섞는다. 서버·meta 에는 싣지 않는다 — «주변 소리 그대로 녹음»(recordingPrefs)과
// 같이 이 브라우저 선호값을 startRec 가 직접 읽어, 녹음 시작 경로가 여럿이어도 모두 같은 값을 따른다.
// 🔴 꺼짐(기본)이면 startRec 는 readPcAudioPref 한 번 말고 이 모듈을 부르지 않는다(종전과 같다).

export const PC_AUDIO_KEY = 'gwMeetingPcAudio';
const DEVICE_PREFIX = 'device:';

// 섞기용 AudioContext 를 «출력 장치 없음»(sinkId none)으로 먼저 만들어 본다 — 스피커 장치가 바뀌어도
// 덜 흔들리게. e2e 에서 문제가 나면 이 상수 하나로 끈다.
const MIX_SINK_NONE = true;

// PC 소리는 원음 그대로 받는다(브라우저 음성 처리가 음악·상대 목소리를 지우지 않게).
const RAW_PROCESSING = { echoCancellation: false, noiseSuppression: false, autoGainControl: false };

const OFF = () => ({ mode: 'off', deviceId: '' });

function mediaDevices() {
  try {
    return typeof navigator === 'undefined' ? null : (navigator.mediaDevices || null);
  } catch {
    return null;
  }
}

function tracksOf(stream) {
  try {
    const list = stream?.getTracks?.();
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}

function audioTracksOf(stream) {
  try {
    if (typeof stream?.getAudioTracks === 'function') {
      const list = stream.getAudioTracks();
      return Array.isArray(list) ? list : [];
    }
  } catch {
    return [];
  }
  return tracksOf(stream).filter((t) => t?.kind === 'audio');
}

function videoTracksOf(stream) {
  try {
    if (typeof stream?.getVideoTracks === 'function') {
      const list = stream.getVideoTracks();
      return Array.isArray(list) ? list : [];
    }
  } catch {
    return [];
  }
  return tracksOf(stream).filter((t) => t?.kind === 'video');
}

function stopTracks(stream) {
  tracksOf(stream).forEach((t) => {
    try { t.stop(); } catch {}
  });
}

export function pcAudioSupported() {
  const md = mediaDevices();
  return {
    share: typeof md?.getDisplayMedia === 'function',
    device: typeof md?.enumerateDevices === 'function',
  };
}

// 없음/빈 값 = 꺼짐 · 'share' = 화면 공유 오디오 · 'device:<deviceId>' = 고른 입력 장치.
// 이 브라우저가 그 방식을 못 쓰거나 장치 id 가 비었으면 꺼짐으로 읽는다.
export function readPcAudioPref() {
  let raw = null;
  try {
    raw = window.localStorage.getItem(PC_AUDIO_KEY);
  } catch (e) { /* 저장소 접근 불가 시 기본(꺼짐) */ }
  if (!raw || typeof raw !== 'string') return OFF();
  const supported = pcAudioSupported();
  if (raw === 'share') return supported.share ? { mode: 'share', deviceId: '' } : OFF();
  if (raw.startsWith(DEVICE_PREFIX)) {
    const deviceId = raw.slice(DEVICE_PREFIX.length);
    if (!deviceId || !supported.device) return OFF();
    return { mode: 'device', deviceId };
  }
  return OFF();
}

export function writePcAudioPref(mode, deviceId = '') {
  try {
    if (mode === 'share') window.localStorage.setItem(PC_AUDIO_KEY, 'share');
    else if (mode === 'device' && deviceId) window.localStorage.setItem(PC_AUDIO_KEY, DEVICE_PREFIX + deviceId);
    else window.localStorage.removeItem(PC_AUDIO_KEY);
  } catch (e) { /* 저장 실패는 무시 */ }
}

// 입력 장치 목록. 'default'·'communications' 는 실제 장치의 별칭이라 뺀다.
// label 은 권한을 받기 전이면 빈 문자열이다. 못 읽으면 [] (던지지 않는다).
export async function listAudioInputs() {
  const md = mediaDevices();
  if (typeof md?.enumerateDevices !== 'function') return [];
  try {
    const list = await md.enumerateDevices();
    return (Array.isArray(list) ? list : [])
      .filter((d) => d && d.kind === 'audioinput')
      .filter((d) => d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications')
      .map((d) => ({ deviceId: String(d.deviceId), label: String(d.label || '') }));
  } catch {
    return [];
  }
}

function isAliasDeviceId(id) {
  return id === 'default' || id === 'communications';
}

// 녹음 마이크(micStream)가 고른 PC 소리 입력 장치(pcDeviceId = 선호값의 장치 id)와 같은 물리 장치인가.
// 같으면 같은 소리가 두 번 섞인다. 마이크 id 가 별칭(default·communications · 비면 default)이면
// enumerateDevices 의 별칭 label 이 ' - ' + <장치 label> 로 끝나는 실제 장치로 푼다(접두는 언어마다 다르다).
// 🔴 groupId 만으로는 같은 장치로 보지 않는다 — 같은 사운드카드의 «스테레오 믹스»가 마이크와 groupId 를
// 공유할 수 있다. groupId 는 후보가 여럿일 때 좁히는 데만 쓴다.
// 스스로 던지지 않는다: 판단 불가는 전부 false(= 종전처럼 섞는다).
export async function isSameInputDevice(micStream, pcDeviceId) {
  try {
    const pcId = typeof pcDeviceId === 'string' ? pcDeviceId : '';
    if (!pcId) return false;
    // 🔴 옵셔널 체이닝 — 시험용 가짜 트랙엔 getSettings 가 없다.
    const settings = audioTracksOf(micStream)[0]?.getSettings?.();
    if (!settings) return false;
    const micId = typeof settings.deviceId === 'string' ? settings.deviceId : '';
    if (micId && micId === pcId) return true;
    if (micId && !isAliasDeviceId(micId)) return false;
    const aliasId = micId || 'default';
    const md = mediaDevices();
    if (typeof md?.enumerateDevices !== 'function') return false;
    const list = await md.enumerateDevices();
    const inputs = (Array.isArray(list) ? list : []).filter((d) => d && d.kind === 'audioinput');
    const alias = inputs.find((d) => d.deviceId === aliasId);
    const aliasLabel = typeof alias?.label === 'string' ? alias.label : '';
    if (!aliasLabel) return false;
    let candidates = inputs.filter((d) => {
      const label = typeof d.label === 'string' ? d.label : '';
      return d.deviceId && !isAliasDeviceId(d.deviceId) && label && aliasLabel.endsWith(' - ' + label);
    });
    if (candidates.length > 1) candidates = candidates.filter((d) => d.groupId && d.groupId === alias.groupId);
    if (candidates.length !== 1) return false;
    return String(candidates[0].deviceId) === pcId;
  } catch {
    return false;
  }
}

// PC 소리 스트림을 받는다. 🔴 마이크보다 **먼저** 부른다 — 화면 공유 창은 사용자 클릭 직후에만 뜬다.
// 스스로 던지지 않는다: 실패는 { stream: null, reason } 으로 돌려주고 녹음은 마이크만으로 이어 간다.
export async function acquirePcAudio(pref) {
  const md = mediaDevices();
  const mode = pref?.mode;
  if (mode === 'share') {
    if (typeof md?.getDisplayMedia !== 'function') {
      return { stream: null, reason: 'error', message: '이 브라우저는 화면 공유 오디오를 지원하지 않습니다' };
    }
    let stream = null;
    try {
      stream = await md.getDisplayMedia({
        video: { frameRate: 1 },
        audio: { ...RAW_PROCESSING },
        systemAudio: 'include',
      });
    } catch (e) {
      const name = e?.name;
      if (name === 'NotAllowedError' || name === 'AbortError') return { stream: null, reason: 'cancelled' };
      return { stream: null, reason: 'error', message: e?.message || String(name || '') };
    }
    if (!stream || audioTracksOf(stream).length === 0) {
      // 창 공유이거나 «오디오 공유»를 안 켰다 — 받은 트랙은 전부 놓는다(공유 표시가 남지 않게).
      stopTracks(stream);
      return { stream: null, reason: 'no-audio' };
    }
    // 비디오는 끄기만 한다(stop 하지 않는다 — 비디오만 멈춰도 오디오가 유지되는지 미실측).
    videoTracksOf(stream).forEach((t) => {
      try { t.enabled = false; } catch {}
    });
    return { stream };
  }
  if (mode === 'device') {
    const id = pref?.deviceId;
    if (!id || typeof md?.getUserMedia !== 'function') return { stream: null, reason: 'no-device' };
    try {
      const stream = await md.getUserMedia({ audio: { deviceId: { exact: id }, ...RAW_PROCESSING } });
      if (!stream) return { stream: null, reason: 'no-device' };
      return { stream };
    } catch {
      return { stream: null, reason: 'no-device' };
    }
  }
  return { stream: null, reason: 'off' };
}

// 마이크·PC 소리를 한 (모노) 스트림으로 섞는다. 만드는 도중 예외면 만든 AudioContext 를 닫고 던진다.
// cleanup 은 원본 트랙(마이크·PC 소리 — 비디오 포함)·섞인 트랙을 멈추고 AudioContext 를 닫는다(두 번 불러도 안전).
export function mixStreams(micStream, pcStream) {
  const Ctor = typeof window === 'undefined' ? null : (window.AudioContext || window.webkitAudioContext);
  if (!Ctor) throw new Error('이 브라우저는 소리 섞기(AudioContext)를 지원하지 않습니다');
  let ctx = null;
  if (MIX_SINK_NONE) {
    try { ctx = new Ctor({ sinkId: { type: 'none' } }); } catch { ctx = null; }
  }
  if (!ctx) ctx = new Ctor();
  try {
    const dest = ctx.createMediaStreamDestination();
    try { dest.channelCount = 1; } catch {}
    const micSource = ctx.createMediaStreamSource(micStream);
    const pcSource = ctx.createMediaStreamSource(pcStream);
    micSource.connect(dest);
    pcSource.connect(dest);
    // 가려진 탭·장치 변경으로 컨텍스트가 멈추면 마이크까지 무음이 된다 — 멈출 때마다 다시 깨운다.
    const keepRunning = () => {
      if (ctx.state === 'running' || ctx.state === 'closed') return;
      try { ctx.resume?.()?.catch?.(() => {}); } catch {}
    };
    ctx.onstatechange = keepRunning;
    if (ctx.state !== 'running') keepRunning();
    let cleaned = false;
    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      try { ctx.onstatechange = null; } catch {}
      stopTracks(micStream);
      stopTracks(pcStream);
      stopTracks(dest.stream);
      try { micSource.disconnect?.(); } catch {}
      try { pcSource.disconnect?.(); } catch {}
      try {
        if (ctx.state !== 'closed') ctx.close?.()?.catch?.(() => {});
      } catch {}
    };
    return { stream: dest.stream, cleanup };
  } catch (e) {
    try { ctx.onstatechange = null; } catch {}
    try { ctx.close?.()?.catch?.(() => {}); } catch {}
    throw e;
  }
}

// 녹음 스트림별 정리 함수(섞인 스트림 → mixStreams 의 cleanup).
const streamCleanups = new WeakMap();

export function registerStreamCleanup(stream, fn) {
  try {
    if (stream && typeof stream === 'object' && typeof fn === 'function') streamCleanups.set(stream, fn);
  } catch {}
}

// 녹음 스트림을 놓는다 — 등록된 정리가 있으면 부르고, **항상** 그 스트림의 트랙도 멈춘다.
// 스스로 던지지 않는다(null·undefined·getTracks 만 있는 시험용 가짜 스트림 허용).
export function stopRecordingStream(stream) {
  try {
    if (!stream) return;
    let fn = null;
    try { fn = streamCleanups.get(stream) || null; } catch { fn = null; }
    if (fn) {
      try { streamCleanups.delete(stream); } catch {}
      try { fn(); } catch {}
    }
    stopTracks(stream);
  } catch {}
}

// 그 스트림 오디오 트랙이 끝나면(장치 뽑힘·«공유 중지») cb 를 한 번 부른다.
// addEventListener 가 없는 가짜 트랙이면 아무것도 하지 않는다.
export function onTrackEnded(stream, cb) {
  if (typeof cb !== 'function') return;
  let fired = false;
  const fire = () => {
    if (fired) return;
    fired = true;
    try { cb(); } catch {}
  };
  audioTracksOf(stream).forEach((t) => {
    if (typeof t?.addEventListener !== 'function') return;
    try { t.addEventListener('ended', fire, { once: true }); } catch {}
  });
}

export function hasLiveAudio(stream) {
  return audioTracksOf(stream).some((t) => t?.readyState === 'live');
}
